import dayjs from 'dayjs';
import type { TooltipProps } from 'recharts';
import i18n from '../../../../../../base-dictionary';
import { ThroughputType } from '../../../../../../store/strategy-store/strategy-store.type';
import { graphCambridgeBlue, secondaryBase, skyDark } from '../../../../../../styles/design-tokens';
import { Metric } from '../../../../strategy-v2.type';
import { DataKey, type LegendItem } from './initiative-progress-chart.type';

/**
 * Get the color for a specific dataKey in the initiative progress chart
 *
 * @param {string} dataKey - The dataKey of the chart item
 * @param {string} [defaultColor] - The default color to use if no specific color is assigned
 * @returns {string} - The color to use for the chart item
 */
const getItemColor = (dataKey: string, defaultColor?: string): string => {
  switch (dataKey) {
    case 'total':
      return secondaryBase;
    case 'completed':
      return graphCambridgeBlue;
    case 'projection':
      return skyDark;
    default:
      return defaultColor || skyDark;
  }
};

/**
 * Gets the current month and year formatted as a label (e.g. "Oct '24")
 *
 * @returns {string} - A string representing the current month and year in MMM 'YY format
 */
const getCurrentMonthLabel = (): string => {
  const now = dayjs();
  const month = now.format('MMM');
  const year = now.format('YY');

  return `${month} '${year}`;
};

/**
 * Gets the display name for a chart line based on the data key and metric type
 *
 * @param {DataKey} dataKey - The data key identifying which line in the chart (completed/total, regular/future)
 * @param {Metric} metric - The metric type being displayed (tasks or points)
 * @returns {string} The translated display name for the chart line
 */
const getChartLineName = (dataKey: DataKey, metric: Metric): string => {
  switch (dataKey) {
    case DataKey.CompletedRegular:
      return metric === Metric.Tasks ? getChartLineNameT('completed_tasks') : getChartLineNameT('completed_points');
    case DataKey.TotalRegular:
      return metric === Metric.Tasks ? getChartLineNameT('total_tasks') : getChartLineNameT('total_points');
    case DataKey.CompletedFuture:
      return metric === Metric.Tasks
        ? getChartLineNameT('future_completed_tasks')
        : getChartLineNameT('future_completed_points');
    case DataKey.TotalFuture:
      return metric === Metric.Tasks
        ? getChartLineNameT('future_total_tasks')
        : getChartLineNameT('future_total_points');
    default:
      return i18n.t('strategy.initiatives.labels.no_data');
  }
};

/**
 * Helper function to get translated chart line names
 *
 * @param {string} key - The translation key suffix for the chart line name
 * @returns {string} The translated text for the chart line name
 */
const getChartLineNameT = (key: string): string => i18n.t(`strategy.initiative_performance.chart_labels.${key}`);

/**
 * Gets the legend items configuration for the initiative progress chart
 *
 * @returns {LegendItem[]} An array of legend items containing data keys, throughput types, and colors
 */
const getLegendItems = (): LegendItem[] => {
  return [
    {
      dataKey: DataKey.TotalRegular,
      throughputType: ThroughputType.Total,
      color: secondaryBase,
    },
    {
      dataKey: DataKey.CompletedRegular,
      throughputType: ThroughputType.Completed,
      color: graphCambridgeBlue,
    },
  ];
};

/**
 * Determines if the tooltip is showing a transitional period between regular and future data
 *
 * @param {TooltipProps<number, string>['payload']} payload - The tooltip payload containing data points
 * @returns {boolean} True if the tooltip shows both regular and future data for either completed or total metrics
 */
const isTransitionalTooltip = (payload: TooltipProps<number, string>['payload']): boolean => {
  if (!payload) {
    return false;
  }

  const payloadKeys = payload.map((entry) => entry.dataKey);
  const hasCompletedKeys = [DataKey.CompletedRegular, DataKey.CompletedFuture].every((key) =>
    payloadKeys.includes(key),
  );
  const hasTotalKeys = [DataKey.TotalRegular, DataKey.TotalFuture].every((key) => payloadKeys.includes(key));

  return hasCompletedKeys || hasTotalKeys;
};

export {
  getChartLineName,
  getChartLineNameT,
  getCurrentMonthLabel,
  getItemColor,
  getLegendItems,
  isTransitionalTooltip,
};
